import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import SendBookingRequestsStyle from "./SendBookingRequestsStyle";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Button,
  Grid,
  TextField
} from "@mui/material";
import { useSelector } from "react-redux";
import { getTerminals } from "../../redux/slice/terminalSlice";
import { useAppDispatch } from "../../redux/dispatch/useAppDispatch";
import { ITerminal } from "../../api/terminal/ITerminal";
import { sendBooking } from "../../redux/slice/bookingSlice";
import RootState from "../../redux/types/rootState";
import { useState } from "react";

interface SendBookingRequestProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SendBookingRequests({
  isOpen,
  onClose,
}: SendBookingRequestProps) {
  const dispatch = useAppDispatch();

  const classes = SendBookingRequestsStyle();

  const selectedBookings = useSelector(
    (state: RootState) => state.bookings.selectedBookings
  );

  const bookingResponse = useSelector(
    (state: RootState) => state.bookings.bookingsResponse
  );

  const hasBookings = bookingResponse === undefined || bookingResponse === null ? false : true

  const terminalState = useSelector((state: RootState) => state.terminal);

  const validationCheck = () => {
    let i = 0;
    selectedBookings.forEach((booking) => {
      if (booking.bookingValidations.length > 0) i = i + 1;
    });
    return i;
  };

  React.useEffect(() => {
    if (!terminalState.isLoading && terminalState.routes == null && hasBookings) {
      dispatch(getTerminals());
    }
  }, [dispatch, terminalState, hasBookings]);

  const originTypes: ITerminal[] | null =
    terminalState?.routes?.origins ?? null;
  const destinationTypes: ITerminal[] | null =
    terminalState?.routes?.destinations ?? null;

  const [originType, setOriginType] = React.useState("");
  const [destinationType, setDestinationType] = React.useState("");
  const [originError, setOriginError] = React.useState(false);
  const [destinationError, setDestinationError] = React.useState(false);
  const [dateError, setDateError] = React.useState(false);

  const getDefaultDate = () => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 1);
    return currentDate;
  };

  const [loadingDate, setLoadingDate] = useState<Date | null>(getDefaultDate());

  const handleChangeForOriginType = (event: SelectChangeEvent) => {
    setOriginType(event.target.value as string);
    setOriginError(false);
  };

  const handleChangeForDestinationType = (event: SelectChangeEvent) => {
    setDestinationType(event.target.value as string);
    setDestinationError(false);
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const countOfValidError = validationCheck();

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const offset = date.getTimezoneOffset();
    date = new Date(date.getTime() - offset * 60 * 1000);
    return date.toISOString().split("T")[0];
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLoadingDate(value ? new Date(value) : null);
    if (value !== null) setDateError(false);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent className={classes.dialogContent}>
        <form>
          <Box className={classes.titlesBoxStyle}>
            <Typography className={classes.typographyFirst} variant="h6">
              Send Booking Request
            </Typography>
          </Box>
          <Box className={classes.titlesBoxStyle}>
            <Typography
              className={classes.typographySecond}
              variant="subtitle1"
            >
              {selectedBookings.length} units are selected.
            </Typography>
            {countOfValidError ? (
              <Typography
                className={classes.typographySecond}
                variant="subtitle1"
                id="error-unit-text"
              >
                {countOfValidError} units have error.
              </Typography>
            ) : null}
          </Box>

          <Grid container>
            <Grid item className={classes.textField}>
              <TextField
                id="loadingDate"
                type="date"
                className={classes.textField}
                InputLabelProps={{
                  shrink: true,
                }}
                label="Loading Date"
                value={formatDate(loadingDate)}
                onChange={handleDateChange}
                InputProps={{
                  inputProps: {
                    min: new Date(new Date().setDate(new Date().getDate()))
                      .toISOString()
                      .split("T")[0],
                  },
                }}
                error={dateError}
              />
            </Grid>

            <Grid item className={classes.dropdownGrid}>
              <FormControl className={classes.formControl} error={originError}>
                <InputLabel id="originTypeLabel">Origin</InputLabel>
                <Select
                  labelId="originTypeLabel"
                  id="originType"
                  value={originType}
                  label="Origin"
                  onChange={handleChangeForOriginType}
                  MenuProps={MenuProps}
                >
                  {originTypes ? (
                    originTypes.map((originType) => (
                      <MenuItem
                        key={originType.code}
                        value={originType.code}
                        className={classes.inputLabelAndMenuItems}
                      >
                        {originType.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                </Select>
                {originError && (
                  <Typography variant="body2" color="error">
                    Please Select Origin
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid
              item
              className={classes.dropdownGrid}
              sx={{ paddingTop: "1rem" }}
            >
              <FormControl
                className={classes.formControl}
                error={destinationError}
              >
                <InputLabel id="destinationTypeLabel">Destination</InputLabel>
                <Select
                  labelId="destinationTypeLabel"
                  id="destinationType"
                  value={destinationType}
                  label="Destination"
                  onChange={handleChangeForDestinationType}
                  MenuProps={MenuProps}
                >
                  {destinationTypes ? (
                    destinationTypes.map((destinationType) => (
                      <MenuItem
                        key={destinationType.code}
                        value={destinationType.code}
                        className={classes.inputLabelAndMenuItems}
                      >
                        {destinationType.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                </Select>
                {destinationError && (
                  <Typography variant="body2" color="error">
                    Please Select Destination
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.actionButtonGrid}>
            <Button className={classes.buttons} onClick={onClose}>
              Cancel
            </Button>
            <Button
              className={classes.buttons}
              onClick={() => {
                const isOriginTypeEmpty = originType === "";
                const isDestinationTypeEmpty = destinationType === "";
                const isLoadingDateEmpty = loadingDate === null;
                setDateError(isLoadingDateEmpty);
                setOriginError(isOriginTypeEmpty);
                setDestinationError(isDestinationTypeEmpty);

                if (
                  !isOriginTypeEmpty &&
                  !isDestinationTypeEmpty &&
                  loadingDate !== null
                ) {
                  dispatch(
                    sendBooking({
                      origin: originType,
                      destination: destinationType,
                      loadingDate: formatDate(loadingDate),
                    })
                  );
                  onClose();
                }
              }}
            >
              Send
            </Button>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}
